import { Component, OnInit, inject} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { QuestionBaseService } from '../../../../api/api-services/question-base.service';
import { IExamQuestionIm } from '../../../../api/exam-question-im.model';
import { GetQuestionCategoriesService } from '../../../shared/api/get-question-categories.service';
import { AvailableExamsService } from '../../../../api/api-services/available-exams.service';
import { IQuestionCategories } from '../../../shared/models/question-categories';
import { IExamViewModel } from '../../../../api/exam-view-model.model';
import { knowledgeTypeOptions, pointsOptions } from '../../../shared/helpers/constant-options';
import { QuestionDatabaseDataService } from '../question-database-data.service';

interface UploadFileResultVm {
  publicVisible: boolean;
  success: boolean;
  size: number;
  fileUrl: string;
}

@Component({
  selector: 'app-add-question',
  templateUrl: './add-question.component.html',
  styleUrl: './add-question.component.scss'
})
export class AddQuestionComponent implements OnInit {

  private _questionBaseService = inject(QuestionBaseService);
  private _getQuestionCategories = inject(GetQuestionCategoriesService);
  private _questionDatabaseDataService = inject(QuestionDatabaseDataService);
  private _availableExamsService = inject(AvailableExamsService);
  private _messageService = inject(MessageService);
  private _dialogRef = inject(DynamicDialogRef);
  private _http = inject(HttpClient);
  private _fb = inject(FormBuilder);

  public questionForm!: FormGroup;
  public showAddMediaLanguageDropdown = false;
  public selectedNewMediaLanguage = '';
  public environment = environment;
  public maxTranslations = 3;
  public currentStep = 1;
  public loading = false;

  public selectedImageFile: File | null = null;
  public uploadedMediaUrl: string = '';

  public translationLanguages = [
    { code: 'en', name: 'Angielski (UK)' },
    { code: 'de', name: 'Niemiecki' },
    { code: 'ua', name: 'Ukraiński' }
  ];

  public questionCategories: IQuestionCategories[] = this._questionDatabaseDataService.questionCategories();
  public availableExams: IExamViewModel[] = this._questionDatabaseDataService.availableExams();
  public pointsOptions = pointsOptions;
  public knowledgeTypeOptions = knowledgeTypeOptions;

  ngOnInit(): void {
    this.initForms();
  }


  private initForms(): void {
    this.questionForm = this._fb.group({
      questionNumber: ['', Validators.required],
      questionCategoryGuid: ['', Validators.required],
      category: [[], Validators.required],
      points: ['', Validators.required],
      knowledgeType: ['', Validators.required],
      questionText: ['', Validators.required],
      answerA: [''],
      answerB: [''],
      answerC: [''],
      correctAnswer: ['', Validators.required],
      explainPL: [''],
      translations: this._fb.array([])
    });

    this.questionForm.get('knowledgeType')?.valueChanges.subscribe(value => {
      this.updateAnswerValidation(value);
    });
  }

  private updateAnswerValidation(knowledgeType: number): void {
    const answerA = this.questionForm.get('answerA');
    const answerB = this.questionForm.get('answerB');
    const answerC = this.questionForm.get('answerC');

    if (knowledgeType === 2) {
      answerA?.setValidators([Validators.required]);
      answerB?.setValidators([Validators.required]);
      answerC?.setValidators([Validators.required]);
    } else if (knowledgeType === 1) {
      answerA?.clearValidators();
      answerB?.clearValidators();
      answerC?.clearValidators();
      answerA?.setValue('');
      answerB?.setValue('');
      answerC?.setValue('');
    }

    answerA?.updateValueAndValidity();
    answerB?.updateValueAndValidity();
    answerC?.updateValueAndValidity();
  }

  get translationsArray(): FormArray {
    return this.questionForm.get('translations') as FormArray;
  }

  createTranslationGroup(language: any): FormGroup {
    return this._fb.group({
      languageCode: [language.code, Validators.required],
      questionText: [''],
      answerA: [''],
      answerB: [''],
      answerC: [''],
      explain: [''],
      collapsed: [false]
    });
  }

  removeTranslationForm(index: number): void {
    this.translationsArray.removeAt(index);
  }

  addNewTranslationForm(): void {
    if (this.translationsArray.length < this.maxTranslations) {
      const translationGroup = this._fb.group({
        languageCode: ['', [Validators.required]],
        questionText: [''],
        answerA: [''],
        answerB: [''],
        answerC: [''],
        explain: [''],
        collapsed: [false]
      });
      this.translationsArray.push(translationGroup);
    }
  }

  toggleTranslationCollapse(index: number): void {
    const translation = this.translationsArray.at(index);
    const currentValue = translation.get('collapsed')?.value;
    translation.patchValue({ collapsed: !currentValue });
  }

  isTranslationCollapsed(index: number): boolean {
    const translation = this.translationsArray.at(index);
    return translation ? translation.get('collapsed')?.value : false;
  }

  canAddMoreTranslations(): boolean {
    return this.translationsArray.length < this.maxTranslations;
  }

  getAvailableLanguagesForTranslation(currentIndex: number): any[] {
    const usedLanguages = this.translationsArray.controls
      .map((control, index) => index !== currentIndex ? control.get('languageCode')?.value : null)
      .filter(code => code !== null);
    return this.translationLanguages.filter(lang => !usedLanguages.includes(lang.code));
  }

  getLanguageName(languageCode: string): string {
    if (!languageCode) {
      return '';
    }
    const language = this.translationLanguages.find(lang => lang.code === languageCode);
    return language?.name || languageCode;
  }

  getCorrectAnswerOptions(): { label: string; value: string }[] {
    const knowledgeType = this.questionForm.get('knowledgeType')?.value;
    if (knowledgeType === 1) {
      return [
        { label: 'Tak', value: 'Tak' },
        { label: 'Nie', value: 'Nie' }
      ];
    } else {
      return [
        { label: 'A', value: 'A' },
        { label: 'B', value: 'B' },
        { label: 'C', value: 'C' }
      ];
    }
  }

  onKnowledgeTypeChange(event: any): void {
    const knowledgeType = event.value;
    this.updateAnswerValidation(knowledgeType);
    this.questionForm.get('correctAnswer')?.setValue('');
    
    if (knowledgeType === 1) {
      this.translationsArray.controls.forEach(control => {
        control.get('answerA')?.setValue('');
        control.get('answerB')?.setValue('');
        control.get('answerC')?.setValue('');
      });
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.questionForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.canProceedToNextStep()) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  canProceedToNextStep(): boolean {
    const knowledgeType = this.questionForm.get('knowledgeType')?.value;
    
    if (!this.questionForm.get('questionNumber')?.valid) return false;
    if (!this.questionForm.get('questionCategoryGuid')?.valid) return false;
    if (!this.questionForm.get('category')?.valid) return false;
    if (!this.questionForm.get('points')?.valid) return false;
    if (!this.questionForm.get('knowledgeType')?.valid) return false;
    if (!this.questionForm.get('questionText')?.valid) return false;
    if (!this.questionForm.get('correctAnswer')?.valid) return false;
    
    if (knowledgeType === 2) {
      if (!this.questionForm.get('answerA')?.valid) return false;
      if (!this.questionForm.get('answerB')?.valid) return false;
      if (!this.questionForm.get('answerC')?.valid) return false;
    }
    
    for (let i = 0; i < this.translationsArray.length; i++) {
      const translation = this.translationsArray.at(i);
      
      if (!translation.get('languageCode')?.value) {
        return false;
      }
      
      if (!translation.get('questionText')?.value || 
          translation.get('questionText')?.value.trim() === '') {
        return false;
      }
      
      if (knowledgeType === 2) {
        if (!translation.get('answerA')?.value || 
            translation.get('answerA')?.value.trim() === '') {
          return false;
        }
        if (!translation.get('answerB')?.value || 
            translation.get('answerB')?.value.trim() === '') {
          return false;
        }
        if (!translation.get('answerC')?.value || 
            translation.get('answerC')?.value.trim() === '') {
          return false;
        }
      }
    }
    
    return true;
  }

  onImageSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedImageFile = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name}`
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedImageFile = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name}`
      });
    }
  }



  private async uploadFileToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(
          `${environment.webApiUrl}/UploadMedia`,
          formData
        )
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Plik został przesłany'
        });
        const urlParams = new URLSearchParams(response.fileUrl.split('?')[1]);
        const fileName = urlParams.get('imageName') || response.fileUrl.split('/').pop() || response.fileUrl;
        return fileName;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Nie udało się przesłać pliku'
      });
      throw error;
    }
  }

  async save(): Promise<void> {
    console.log('xx1')
    if (this.questionForm.invalid) {
      this.markFormGroupTouched(this.questionForm);
      return;
    }
console.log('xx2')
this.loading = true;

try {
  if (this.selectedImageFile) {
    this.uploadedMediaUrl = await this.uploadFileToServer(this.selectedImageFile);
  }
  
  console.log('xx3')
  const formValue = this.questionForm.value;
  const translations = this.translationsArray.value;
  console.log('xx4')

      const selectedExams = Array.isArray(formValue.category) ? formValue.category : [];
      const questionData: IExamQuestionIm = {
        questionNumber: formValue.questionNumber,
        questionText: formValue.questionText,
        answerA: formValue.answerA,
        answerB: formValue.answerB,
        answerC: formValue.answerC,
        questionENG: translations.find((t: any) => t.languageCode === 'en')?.questionText || '',
        answerENGA: translations.find((t: any) => t.languageCode === 'en')?.answerA || '',
        answerENGB: translations.find((t: any) => t.languageCode === 'en')?.answerB || '',
        answerENGC: translations.find((t: any) => t.languageCode === 'en')?.answerC || '',
        questionDE: translations.find((t: any) => t.languageCode === 'de')?.questionText || '',
        answerDEA: translations.find((t: any) => t.languageCode === 'de')?.answerA || '',
        answerDEB: translations.find((t: any) => t.languageCode === 'de')?.answerB || '',
        answerDEC: translations.find((t: any) => t.languageCode === 'de')?.answerC || '',
        questionUA: translations.find((t: any) => t.languageCode === 'ua')?.questionText || '',
        answerUAA: translations.find((t: any) => t.languageCode === 'ua')?.answerA || '',
        answerUAB: translations.find((t: any) => t.languageCode === 'ua')?.answerB || '',
        answerUAC: translations.find((t: any) => t.languageCode === 'ua')?.answerC || '',
        correctAnswer: formValue.correctAnswer,
        media: this.uploadedMediaUrl,
        category: selectedExams.map((e: any) => e.examName).join(','),
        points: formValue.points,
        questionCategoryGuid: formValue.questionCategoryGuid,
        knowledgeType: formValue.knowledgeType,
        explainPL: formValue.explainPL || '',
        explainENG: translations.find((t: any) => t.languageCode === 'en')?.explain || '',
        explainDE: translations.find((t: any) => t.languageCode === 'de')?.explain || '',
        explainUA: translations.find((t: any) => t.languageCode === 'ua')?.explain || '',
  };
  
      console.log('xx5', questionData)

      this._questionBaseService.post(questionData).subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Pytanie zostało dodane'
          });
          this._dialogRef.close(true);
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się dodać pytania'
          });
          this.loading = false;
        }
      });
    } catch (error) {
      this.loading = false;
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  cancel(): void {
    this._dialogRef.close(false);
  }
}
